import { coerceArray } from '@angular/cdk/coercion';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FacilityService } from 'src/app/core/services/facility.service';
import { HttpHeadersService } from 'src/app/core/shared-service/http-headers.service';
import { environment } from '../../../environments/environment';
import { CommonUtil } from '../Utils/common-util';
import { conditions, feature, featureFlags } from './feature.flags.model';

@Injectable({
  providedIn: 'root'
})
export class FeatureFlagsService {
  private featureFlags: featureFlags;
  private isInitialized = false;
  private retrieveDataPromise: Promise<any> | undefined;
  constructor(
    private httpClient: HttpClient,
    private httpHeaderService: HttpHeadersService,
    private facilityService: FacilityService,
    private commonUtil: CommonUtil) { }

  get FeatureFlags() {
    return JSON.parse(JSON.stringify(this.featureFlags));
  }

  async initialize(isForced?: boolean) {
    if(!isForced && this.isInitialized) return;
    if(isForced) this.retrieveDataPromise = undefined;
    if(this.retrieveDataPromise) {
      await this.retrieveDataPromise;
      return;
    } 
    try {
      // when websocket is implemented, subscribe to event that sends the updated features
      // otherwise, poll every 3 hours
      this.retrieveDataPromise = this.getFetureFlags();
      this.featureFlags = await this.retrieveDataPromise;
      if(!this.featureFlags) this.featureFlags = new featureFlags();
      this.featureFlags?.features?.forEach(f => f.conditions?.forEach(c => conditions.ensure(c)));
      this.isInitialized = true;
    } catch (error) {
      this.retrieveDataPromise = undefined;
      console.error('API error.')
    }
  }

  isOn(features: string | string []) {
    if(!features || !this.featureFlags) return false;
    let on = coerceArray(features).every(name => {
      const feature = this.featureFlags.features.find(f => f.name === name);
      return !feature || !feature.enabled; // if feature flag is deleted or disabled, it's always ON
    });
    if(!on) {
      const currentFacility = this.facilityService.selectedFacility();
      if(!currentFacility) return false;
      on = coerceArray(features).every(name => {
        const feature = this.featureFlags.features.find(f => f.name === name);
        if(!feature) return true; // if feature flag is deleted or disabled, it's always ON
        return this.checkFeature(feature);
      });
    }
    return on;
  }

  isOff(feature: string | string []) {
    return !this.isOn(feature);
  }
  
  isAnyOn(features: string[]) {
    if(!features || !this.featureFlags) return false;
    let on = features.some(name => {
      const feature = this.featureFlags.features.find(f => f.name === name);
      return !feature || !feature.enabled; // if feature flag is deleted or disabled, it's always ON
    });
    if(!on) {
      const currentFacility = this.facilityService.selectedFacility();
      if(!currentFacility) return false;
      on = features.some(name => {
        const feature = this.featureFlags.features.find(f => f.name === name);
        if(!feature) return true; // if feature flag is deleted or disabled, it's always ON
        return this.checkFeature(feature);
      });
    }
    return on;
  }

  updateFeatureFlags(featFlags: featureFlags) {
    if(!featFlags) return;
    const copy = featureFlags.clone(featFlags);
    this.featureFlags = copy;
    this.featureFlags?.features?.forEach(f => f.conditions?.forEach(c => conditions.ensure(c)));
    const featData = {
      "featureFlagsVO" : [{ 
        "fieldName" : "FeatureFlags",
        "fieldData": JSON.stringify(copy)
      }]
    }
    const headers = this.httpHeaderService.bearerHttpAuthorizationHeader();
    return this.httpClient.post(`${environment.servicePath}/maintenance/featureFlags/update`, featData, { headers }).toPromise();
  }

  private getFetureFlags(): Promise<any> {
    const headers = this.httpHeaderService.bearerHttpAuthorizationHeader();
    return this.httpClient.get(`${environment.servicePath}/maintenance/featureFlags`,{ headers }).toPromise();
  }

  private checkFeature(feature: feature) {
    const currentFacility = this.facilityService.selectedFacility();
    if(currentFacility?.summary) {
      const email = this.commonUtil.rootScopeData.currentUser.emailAddress.toString();
      const country = currentFacility.summary.countryIsoCode;
      //const consoles = currentFacility.consoleEquipments?.map(c => c.consoleType);
      const consoles = coerceArray(this.commonUtil.rootScopeData.lastSelectedConsole);
      const facilityId = currentFacility.facilityId;
      return feature.conditions.some(c => {
        if(c.users?.length && !c.users.includes(email)) return false;
        if(c.userGroups?.length && !c.userGroups.some(g => this.userPartOfGroup(email, g))) return false;
        if(c.countries?.length && !c.countries.includes(country)) return false;
        if(c.excludedCountries?.length && c.excludedCountries.includes(country)) return false;
        if(c.countryGroups?.length && !c.countryGroups.some(g => this.countryPartOfGroup(country, g))) return false;
        if(c.consoleTypes?.length && (!console || !c.consoleTypes.some(c => consoles.includes(c)))) return false;      
        if(c.facilityIds?.length && !c.facilityIds.includes(facilityId)) return false;
        return true;
      });
    }
    return false;
  }

  private userPartOfGroup(email: string, userGroup: string) {
    return this.featureFlags.userGroups?.filter(g => g.name === userGroup)?.some(g => g.users?.includes(email));
  }

  private countryPartOfGroup(country: string, countryGroup: string) {
    return this.featureFlags.countryGroups?.filter(g => g.name === countryGroup)?.some(g => g.countries?.includes(country));
  }
}
